import { Line } from '@react-three/drei';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import * as THREE from 'three';

import {
  rainfallGullyTaggingAtom,
  rainfallShowOverflowArrowsAtom,
} from '@/atoms/rainfall.atom';
import type { LayerData } from '@/atoms';
import { computeMapOrigin } from '@/core';
import type { FeaturesQuery } from '@/graphql/graphql';
import { RAINFALL_OVERFLOW_ARROW_COLOUR, isPondDrained } from '@/lib/rainfallStyling';

type Feature = FeaturesQuery['featuresByLayer'][number];

interface Props {
  features: Feature[];
  layers: LayerData[] | null;
}

interface ArrowSegment {
  fromId: string;
  toId: string;
  start: [number, number, number];
  end: [number, number, number];
}

const ARROW_COLOUR = new THREE.Color(RAINFALL_OVERFLOW_ARROW_COLOUR);

/**
 * 3D companion to the 2D overflow-arrow overlay rendered by `MapView`.
 * Walks the loaded rainfall pond features and draws a thin line from
 * each pond's centroid to its downstream pond's centroid. Each arrow
 * is rendered with a short cone arrowhead at its midpoint so direction
 * reads at any camera angle.
 *
 * Coordinates: features carry full project-CRS XYZ in `geometry.coordinates`
 * (the worker attached `overflowZ` to every pond polygon vertex). We
 * subtract the shared `mapOrigin` from each centroid so the resulting
 * world-space vectors stay near zero — same float-jitter mitigation
 * the rest of the 3D scene uses (see `computeMapOrigin`).
 *
 * Visibility is gated by:
 *   - `rainfallShowOverflowArrowsAtom` (user toggle in controls panel)
 *   - At least one rainfall layer must contain pond features whose
 *     `overflowsIntoPondId` resolves to another known pond.
 */
export function RainfallOverflowArrows3D({ features, layers }: Props) {
  const show = useAtomValue(rainfallShowOverflowArrowsAtom);
  const gullies = useAtomValue(rainfallGullyTaggingAtom);

  // Reuse the same floating-origin trick as GeoJson.tsx — compute the
  // per-render mapOrigin from the same feature set so our arrows sit
  // in the same world-space frame as the pond polygons they connect.
  const mapOrigin = useMemo(
    () => (features?.length ? computeMapOrigin(features) : new THREE.Vector3(0, 0, 0)),
    [features],
  );

  const rainfallLayerIds = useMemo(() => {
    const ids = new Set<string>();
    for (const l of layers ?? []) {
      const meta = (l.metadata ?? {}) as { type?: string };
      if (meta.type === 'RAINFALL_VECTOR') ids.add(l.id);
    }
    return ids;
  }, [layers]);

  const segments = useMemo<ArrowSegment[]>(() => {
    if (!show || rainfallLayerIds.size === 0 || !features?.length) return [];
    // Build pondId → centroid map from project-CRS coordinates. We use
    // the first ring of the pond polygon and average its vertices —
    // good enough at the scales arrows are drawn at, and avoids
    // pulling in a centroid library for one helper.
    const centroidByPondId = new Map<string, THREE.Vector3>();
    for (const f of features) {
      if (!rainfallLayerIds.has(f.layerId as string)) continue;
      const props = (f.properties ?? {}) as Record<string, unknown>;
      if (props.kind !== 'pond') continue;
      const pondId = props.pondId as string | undefined;
      if (!pondId) continue;
      const geom = f.geometry as unknown as { type?: string; coordinates?: any } | null;
      const ring = pondOuterRing(geom);
      if (!ring || ring.length === 0) continue;
      let sx = 0;
      let sy = 0;
      let sz = 0;
      for (const v of ring) {
        sx += v[0];
        sy += v[1];
        sz += v[2] ?? 0;
      }
      const inv = 1 / ring.length;
      centroidByPondId.set(
        pondId,
        new THREE.Vector3(sx * inv - mapOrigin.x, sy * inv - mapOrigin.y, sz * inv - mapOrigin.z),
      );
    }

    // Only render arrows for the worker's authoritative chain
    // (`overflowsIntoPondId`). The earlier "infer terminal-pond
    // overflow target" heuristic was removed — too noisy to trust
    // in a demo. Same logic as the 2D map's
    // `buildRainfallOverflowArrows` so the two views agree.
    const out: ArrowSegment[] = [];
    for (const f of features) {
      if (!rainfallLayerIds.has(f.layerId as string)) continue;
      const props = (f.properties ?? {}) as Record<string, unknown>;
      if (props.kind !== 'pond') continue;
      const fromId = props.pondId as string | undefined;
      const toId = props.overflowsIntoPondId as string | undefined | null;
      if (!fromId || !toId) continue;
      if (isPondDrained(props, gullies)) continue;
      const start = centroidByPondId.get(fromId);
      const end = centroidByPondId.get(toId);
      if (!start || !end) continue;
      out.push({
        fromId,
        toId,
        start: [start.x, start.y, start.z],
        end: [end.x, end.y, end.z],
      });
    }
    return out;
  }, [features, rainfallLayerIds, show, mapOrigin, gullies]);

  if (segments.length === 0) return null;

  // Render the whole arrow group translated by `mapOrigin`, mirroring
  // the polygon-mesh pattern in GeoJson.tsx (`position={mapOriginArray}`
  // on each mesh so its locally-shifted geometry resolves to absolute
  // world coords). Without this the arrows render at floating-origin
  // coords (~near zero Z) while the pond polygons render at absolute
  // world Z (e.g. survey elevation 200m), so the arrows sit hundreds
  // of metres below the ponds — invisible at any reasonable camera
  // angle. The bug is silent because the geometry is technically
  // correct relative to the group, just at the wrong world position.
  return (
    <group position={[mapOrigin.x, mapOrigin.y, mapOrigin.z]}>
      {segments.map((seg) => (
        <ArrowMesh key={`${seg.fromId}->${seg.toId}`} start={seg.start} end={seg.end} />
      ))}
    </group>
  );
}

function ArrowMesh({
  start,
  end,
}: {
  start: [number, number, number];
  end: [number, number, number];
}) {
  // Build a single LineSegments + a cone for the arrowhead, both
  // memoised together so React doesn't churn the geometry on each
  // re-render. Camera-facing arrowheads aren't worth the complexity
  // here — the cone is oriented along the segment, which reads
  // correctly from any reasonable survey-bird's-eye angle.
  const { points, midpoint, direction, length } = useMemo(() => {
    const a = new THREE.Vector3(...start);
    const b = new THREE.Vector3(...end);
    const mid = a.clone().lerp(b, 0.7); // tip at 70% — cone reads in the gap
    const dir = b.clone().sub(a);
    const len = dir.length();
    dir.normalize();
    return {
      points: [
        [a.x, a.y, a.z] as [number, number, number],
        [b.x, b.y, b.z] as [number, number, number],
      ],
      midpoint: mid,
      direction: dir,
      length: len,
    };
  }, [start, end]);

  // Cone's default axis points up (+Y in three.js). We want it to
  // point along `direction`, so build a quaternion that rotates +Y to
  // direction. (Three's Z-up is project convention — see CLAUDE.md —
  // but `setFromUnitVectors` from a +Y reference still produces a
  // valid orientation regardless of which axis is "up" globally.)
  const quaternion = useMemo(() => {
    const q = new THREE.Quaternion();
    q.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction);
    return q;
  }, [direction]);

  // Cone size scales with arrow length so the arrowhead reads at any
  // survey scale — a fixed-metre cone disappears on huge sites and
  // dominates tiny ones. 8% of length, clamped to a sensible window.
  const coneHeight = Math.max(0.5, Math.min(length * 0.08, 6));
  const coneRadius = coneHeight * 0.4;

  return (
    <>
      {/* drei's Line uses Line2 (a thick screen-space line built from
          triangles), which actually renders the requested lineWidth —
          plain `<lineSegments>` + lineBasicMaterial is capped at 1px
          on most WebGL implementations, so the arrows ended up either
          invisible or hairline-thin and indistinguishable from the
          ground.  depthTest:false + high renderOrder keeps them above
          terrain and pond polygons regardless of camera distance. */}
      <Line
        points={points}
        color={RAINFALL_OVERFLOW_ARROW_COLOUR}
        lineWidth={3}
        transparent
        opacity={0.95}
        depthTest={false}
        renderOrder={999}
      />
      <mesh
        position={[midpoint.x, midpoint.y, midpoint.z]}
        quaternion={[quaternion.x, quaternion.y, quaternion.z, quaternion.w]}
        renderOrder={1000}>
        <coneGeometry args={[coneRadius, coneHeight, 12]} />
        <meshBasicMaterial color={ARROW_COLOUR} transparent opacity={0.95} depthTest={false} />
      </mesh>
    </>
  );
}

function pondOuterRing(
  geom: { type?: string; coordinates?: any } | null | undefined,
): number[][] | null {
  if (!geom || !geom.coordinates) return null;
  if (geom.type === 'Polygon') return geom.coordinates[0] ?? null;
  if (geom.type === 'MultiPolygon') return geom.coordinates[0]?.[0] ?? null;
  return null;
}
